import { Suspense, lazy, useMemo  } from 'react'
import type { Density, ViewType } from '@/store/ProjectViewPreferencesStore'
import type { EnrichedTask } from '@/lib/db/rxdb/hooks/entities/tasks'
import type { TaskDocument } from '@/lib/db/rxdb/schemas/task'
import type { SavedFilterDocument } from '@/lib/db/rxdb/hooks'
import { ViewLoadingFallback } from '@/routes/features/projects/components/ViewLoadingFallback'
import { FilterBar } from '@/components/features/filters'
import { TaskPageLayout } from '@/routes/features/tasks/components/TaskPageLayout'
import useProjectFilterStore from '@/store/ProjectFilterStore'
import useProjectViewPreferencesStore from '@/store/ProjectViewPreferencesStore'
import { EMPTY_FILTERS } from '@/lib/types/filter'
import { sortTasks } from '@/routes/features/projects/utils/sortAndGroupTasks'

const ProjectBoardView = lazy(() =>
  import('@/routes/features/projects/components/ProjectBoardView').then((m) => ({
    default: m.ProjectBoardView,
  })),
)
const ProjectCalendarView = lazy(() =>
  import('@/routes/features/projects/components/ProjectCalendarView').then((m) => ({
    default: m.ProjectCalendarView,
  })),
)
const ProjectGanttView = lazy(() =>
  import('@/routes/features/projects/components/ProjectGanttView').then((m) => ({
    default: m.ProjectGanttView,
  })),
)

interface SpaceTasksContentProps {
  spaceId: string
  tasks: Array<EnrichedTask>
  /** The list layout's rows; defaults to `tasks` (sub-tasks flattened in, 28b). */
  listTasks?: Array<EnrichedTask>
  isLoading: boolean
  density: Density
  /**
   * Optional override for which layout to render. If omitted, reads the
   * current preference from the store (standard behaviour). When rendering
   * a saved view URL we pass the view's layout explicitly so a pref update
   * race doesn't flash the wrong layout.
   */
  viewTypeOverride?: ViewType
  /** When rendered from a saved view route, the hydrated saved-filter doc. */
  currentView?: SavedFilterDocument | null
}

export function SpaceTasksContent({
  spaceId,
  tasks,
  listTasks,
  isLoading,
  density,
  viewTypeOverride,
  currentView,
}: SpaceTasksContentProps) {
  const storeViewType = useProjectViewPreferencesStore(
    (s) => s.preferences[spaceId]?.viewType ?? 'list',
  )
  const viewType: ViewType = viewTypeOverride ?? storeViewType

  const filters = useProjectFilterStore(
    (s) => s.filters[spaceId] ?? EMPTY_FILTERS,
  )
  const detailsPanel = useProjectViewPreferencesStore(
    (s) => s.preferences[spaceId]?.detailsPanel ?? 'on',
  )
  const sortBy = useProjectViewPreferencesStore(
    (s) => s.preferences[spaceId]?.sortBy ?? 'manual',
  )
  const sortDirection = useProjectViewPreferencesStore(
    (s) => s.preferences[spaceId]?.sortDirection ?? 'asc',
  )

  const transformItems = useMemo(
    () => (items: Array<TaskDocument>) => sortTasks(items, sortBy, sortDirection),
    [sortBy, sortDirection],
  )

  const defaultProjectId = useMemo(() => {
    const projectFilter = filters.find(
      (f) => f.fieldKey === 'project' && f.operator === 'includes',
    )
    if (!projectFilter || projectFilter.values.length !== 1) return null
    const [projectValue] = projectFilter.values
    return typeof projectValue === 'string' ? projectValue : null
  }, [filters])

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex-shrink-0">
        <FilterBar
          projectId={spaceId}
          pinnedFields={['project']}
          scope="space"
          currentView={currentView}
        />
      </div>
      <div className="flex flex-1 min-h-0 flex-col overflow-hidden">
        {viewType === 'board' ? (
          <Suspense fallback={<ViewLoadingFallback />}>
            <ProjectBoardView
              projectId={spaceId}
              density={density}
              tasks={tasks}
              isLoading={isLoading}
              disableTaskCreation
            />
          </Suspense>
        ) : viewType === 'calendar' ? (
          <Suspense fallback={<ViewLoadingFallback />}>
            <ProjectCalendarView
              projectId={spaceId}
              density={density}
              tasks={tasks}
              isLoading={isLoading}
            />
          </Suspense>
        ) : viewType === 'gantt' ? (
          <Suspense fallback={<ViewLoadingFallback />}>
            <ProjectGanttView
              projectId={spaceId}
              density={density}
              tasks={tasks}
              isLoading={isLoading}
            />
          </Suspense>
        ) : (
          <TaskPageLayout
            tasks={listTasks ?? tasks}
            isLoading={isLoading}
            transformItems={transformItems}
            navigateToSubtasks={true}
            showViewModeToggle={false}
            showAddTaskInput={true}
            defaultProjectId={defaultProjectId}
            viewModeOverride={detailsPanel === 'on' ? 'split' : 'simple'}
          />
        )}
      </div>
    </div>
  )
}
